import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { API_BASE_URL } from "../apiConfig";
import styles from "./AdminPage.module.css";
import bubbleStyles from "../styles/Bubbles.module.css";

function AdminPage() {
  const [announcements, setAnnouncements] = useState([]);
  const [contact, setContact] = useState({
    phoneNumbers: [],
    email: "",
  });
  const [profiles, setProfiles] = useState([]);
  const [savingAnnouncements, setSavingAnnouncements] = useState(false);
  const [updateAnnouncement, setAnnouncementMessages] = useState("");
  const [savingContact, setSavingContact] = useState(false);
  const [contactMessage, setContactMessage] = useState("");
  const [savingProfiles, setSavingProfiles] = useState(false);
  const [profilesMessage, setProfilesMessage] = useState("");
  const [validationErrors, setValidationErrors] = useState({});
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    fetch("/admin-check", { credentials: "include" }).then((res) => {
      if (res.status !== 200) {
        navigate("/admin-login", { state: { from: location.pathname } });
      }
    });
  }, []);

  useEffect(() => {
    axios
      .get(`${API_BASE_URL}/api/announcements`)
      .then((res) => setAnnouncements(res.data))
      .catch((err) => console.error("Error fetching announcements:", err));

    axios
      .get(`${API_BASE_URL}/api/contact`)
      .then((res) => {
        const c = res.data || {};
        setContact({
          phoneOperators: Array.isArray(c.phoneOperators)
            ? c.phoneOperators
            : [],
          email: c.email || "",
        });
      })
      .catch((err) => console.error("Error fetching contact:", err));

    axios
      .get(`${API_BASE_URL}/api/profiles`)
      .then((res) => {
        const collapsed = {};
        res.data.forEach((p) => (collapsed[p.id] = true));
        setProfiles(res.data);
      })
      .catch((err) => console.error("Error fetching profiles:", err));
  }, []);

  const handleSaveAnnouncements = async () => {
    setSavingAnnouncements(true);
    setAnnouncementMessages("");

    try {
      const maxId = announcements.reduce((max, u) => {
        return typeof u.id === "number" && u.id > max ? u.id : max;
      }, 0);

      let nextId = maxId + 1;

      const validAnnouncements = announcements
        .filter((u) => !u.queuedForDeletion)
        .filter(
          (u) => typeof u.announcement === "string" && u.announcement.trim(),
        )
        .map((u) => ({
          id: typeof u.id === "number" ? u.id : nextId++,
          announcement: u.announcement.trim(),
        }));

      const response = await fetch(`${API_BASE_URL}/api/announcements`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ announcements: validAnnouncements }),
      });

      if (!response.ok) throw new Error("Failed to save announcements");

      // ⭐ Re-fetch to sync UI with DB
      const refreshed = await axios.get(`${API_BASE_URL}/api/announcements`);
      setAnnouncements(refreshed.data);

      setAnnouncementMessages("✅ Announcements saved successfully");
    } catch (err) {
      console.error(err);
      setAnnouncementMessages("❌ Failed to save announcements");
    } finally {
      setSavingAnnouncements(false);
      setTimeout(() => setAnnouncementMessages(""), 3000);
    }
  };

  const handleSaveContact = async () => {
    setSavingContact(true);
    setContactMessage("");
    try {
      const response = await fetch(`${API_BASE_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(contact),
      });

      if (!response.ok) throw new Error("Failed to save contact info");
      setContactMessage("✅ Contact info saved successfully");
    } catch (err) {
      setContactMessage("❌ Failed to save contact info");
      console.error(err);
    } finally {
      setSavingContact(false);
      setTimeout(() => setContactMessage(""), 3000);
    }
  };

  const handleSaveProfile = (index) => async () => {
    const profile = profiles[index];
    const errors = {};
    const messages = [];

    // Validation
    if (!profile.name.trim()) {
      errors.name = true;
      messages.push("Name cannot be empty");
    }
    if (/\d/.test(profile.name)) {
      errors.name = true;
      messages.push("Name cannot contain numbers");
    }
    if (
      typeof profile.heightCm === "string" &&
      profile.heightCm.trim() !== "" &&
      /\D/.test(profile.heightCm)
    ) {
      errors.heightCm = true;
      messages.push("Height (cm) must be a valid number");
    }
    if (
      typeof profile.weightKg === "string" &&
      profile.weightKg.trim() !== "" &&
      /\D/.test(profile.weightKg)
    ) {
      errors.weightKg = true;
      messages.push("Weight (kg) must be a valid number");
    }
    if (/\d/.test(profile.ethnicity)) {
      errors.ethnicity = true;
      messages.push("Ehnicity cannot contain numbers");
    }

    try {
      if (messages.length > 0) {
        setValidationErrors({ [profile.id]: errors });
        throw new Error(messages.join(", "));
      }

      setValidationErrors({});
      setSavingProfiles(true);
      setProfilesMessage("");

      const updatedProfile = { ...profile };

      // Remove announcements queued for deletion
      if (Array.isArray(updatedProfile.announcements)) {
        updatedProfile.announcements = updatedProfile.announcements
          .filter((a) => !a.queuedForDeletion)
          .map((a) => ({
            id: a.id,
            announcement: a.announcement.trim(),
          }));
      }

      // Save profile
      const response = await fetch(
        `${API_BASE_URL}/api/profiles/${updatedProfile.id}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(updatedProfile),
        },
      );

      if (!response.ok)
        throw new Error("Network error: Failed to save profile");

      setProfiles((prev) =>
        prev.map((p, i) =>
          i === index
            ? { ...updatedProfile, __status: "✅ Profile saved successfully" }
            : p,
        ),
      );

      setProfilesMessage("✅ Profile saved successfully");
    } catch (err) {
      const newProfiles = [...profiles];
      newProfiles[index].__status = `❌ ${err.message}`;
      setProfiles(newProfiles);
      setProfilesMessage(`❌ ${err.message}`);
      console.error(err);
    } finally {
      setSavingProfiles(false);
      setTimeout(() => {
        setProfiles((prev) => {
          const copy = [...prev];
          if (copy[index]) delete copy[index].__status;
          return copy;
        });

        setProfilesMessage("");
      }, 4000);
    }
  };

  return (
    <div className={styles.adminWrapper}>
      <h1>Admin Page</h1>

      {/* Updates Section */}
      <div className={styles.formSection}>
        <h2>Edit Announcements</h2>

        {announcements.map((item, index) => (
          <div
            key={item.id}
            className={`${styles.inputRow} ${
              item.queuedForDeletion ? styles.queuedDelete : ""
            }`}
          >
            <label
              className={`${styles.fieldLabel} ${styles.announcementIndex}`}
            >
              {index + 1}.
            </label>

            <input
              type="text"
              value={item.announcement}
              onChange={(e) => {
                const updated = [...announcements];
                updated[index].announcement = e.target.value;
                setAnnouncements(updated);
              }}
              className={styles.inputField}
            />

            <span
              className={styles.deleteIcon}
              onClick={() => {
                const updated = [...announcements];
                updated[index].queuedForDeletion =
                  !updated[index].queuedForDeletion;
                setAnnouncements(updated);
              }}
            >
              {item.queuedForDeletion ? "↩️" : "❌"}
            </span>
          </div>
        ))}

        <div className={styles.addRow}>
          <button
            className={styles.button}
            onClick={() => {
              const nextId = announcements.length
                ? Math.max(
                    ...announcements.map((u) =>
                      typeof u.id === "number" ? u.id : 0,
                    ),
                  ) + 1
                : 1;

              setAnnouncements([
                ...announcements,
                { id: nextId, announcement: "" },
              ]);
            }}
          >
            + Announcements
          </button>
        </div>

        <div className={styles.saveRow}>
          <button
            className={styles.button}
            onClick={handleSaveAnnouncements}
            disabled={savingAnnouncements}
          >
            {savingAnnouncements ? "Saving..." : "Save Changes"}
          </button>
        </div>

        {updateAnnouncement && (
          <div className={styles.saveFeedback}>{updateAnnouncement}</div>
        )}
      </div>

      {/* Contact Section */}
      <div className={styles.formSection}>
        <h2>Edit Contact Info</h2>

        {/* PHONE OPERATORS */}
        {contact.phoneOperators?.map((op, index) => (
          <div
            key={op.id}
            className={`${styles.operatorBlock} ${
              op.queuedForDeletion ? styles.queuedDelete : ""
            }`}
          >
            {/* ROW 1 — Name + Phone */}
            <div className={styles.row}>
              <div className={styles.colInline}>
                <span className={styles.inlineLabel}>Name:</span>
                <input
                  type="text"
                  value={op.name}
                  onChange={(e) => {
                    const updated = [...contact.phoneOperators];
                    updated[index].name = e.target.value;
                    setContact((prev) => ({
                      ...prev,
                      phoneOperators: updated,
                    }));
                  }}
                  className={styles.inlineInput}
                />
              </div>

              <div className={styles.colInline}>
                <span className={styles.inlineLabel}>Phone:</span>
                <input
                  type="text"
                  value={op.phoneNumber}
                  onChange={(e) => {
                    const updated = [...contact.phoneOperators];
                    updated[index].phoneNumber = e.target.value;
                    setContact((prev) => ({
                      ...prev,
                      phoneOperators: updated,
                    }));
                  }}
                  className={styles.inlineInput}
                />
              </div>
            </div>

            {/* ROW 2 — Order + Vacation */}
            <div className={styles.row}>
              <div className={styles.colInline}>
                <span className={styles.inlineLabel}>Order:</span>
                <input
                  type="number"
                  min="1"
                  value={op.order || ""}
                  onChange={(e) => {
                    const updated = [...contact.phoneOperators];
                    updated[index].order = Number(e.target.value);
                    setContact((prev) => ({
                      ...prev,
                      phoneOperators: updated,
                    }));
                  }}
                  className={styles.inlineInput}
                />
              </div>

              <div className={styles.colInline}>
                <span className={styles.inlineLabel}>Vacation:</span>
                <input
                  type="checkbox"
                  checked={op.vacation}
                  onChange={(e) => {
                    const updated = [...contact.phoneOperators];
                    updated[index].vacation = e.target.checked;
                    setContact((prev) => ({
                      ...prev,
                      phoneOperators: updated,
                    }));
                  }}
                  className={styles.inlineCheckbox}
                />
              </div>
            </div>

            {/* ROW 3 — Status + Delete */}
            <div className={styles.row}>
              <div className={styles.colInline}>
                <span className={styles.inlineLabel}>Status:</span>
                <input
                  type="text"
                  value={op.statusMessage || ""}
                  onChange={(e) => {
                    const updated = [...contact.phoneOperators];
                    updated[index].statusMessage = e.target.value;
                    setContact((prev) => ({
                      ...prev,
                      phoneOperators: updated,
                    }));
                  }}
                  className={styles.inlineInput}
                />
              </div>

              <div className={styles.colInline}>
                <span className={styles.inlineLabel}>Delete:</span>
                <span
                  className={styles.deleteIcon}
                  onClick={() => {
                    const updated = [...contact.phoneOperators];
                    updated[index].queuedForDeletion =
                      !updated[index].queuedForDeletion;
                    setContact((prev) => ({
                      ...prev,
                      phoneOperators: updated,
                    }));
                  }}
                >
                  {op.queuedForDeletion ? "↩️" : "❌"}
                </span>
              </div>
            </div>

            <hr className={styles.operatorDivider} />
          </div>
        ))}

        {/* ADD OPERATOR BUTTON */}
        <div className={styles.addRow}>
          <button
            className={styles.button}
            onClick={() => {
              if (contact.phoneOperators.length >= 3) return;

              const newOp = {
                id: Date.now(),
                order: contact.phoneOperators.length + 1,
                name: "",
                phoneNumber: "",
                vacation: false,
                statusMessage: "",
              };

              setContact((prev) => ({
                ...prev,
                phoneOperators: [...prev.phoneOperators, newOp],
              }));
            }}
          >
            + PO
          </button>
        </div>

        {/* EMAIL */}
        <div className={styles.inputRow}>
          <label className={styles.fieldLabel}>Email</label>
          <input
            type="email"
            value={contact.email}
            onChange={(e) =>
              setContact((prev) => ({ ...prev, email: e.target.value }))
            }
            className={styles.inputField}
          />
        </div>

        {/* SAVE BUTTON */}
        <div className={styles.saveRow}>
          <button
            className={styles.button}
            onClick={handleSaveContact}
            disabled={savingContact}
          >
            {savingContact ? "Saving..." : "Save Changes"}
          </button>
        </div>

        {contactMessage && (
          <div className={styles.saveFeedback}>{contactMessage}</div>
        )}
      </div>

      {/* Profile Section */}
      <div className={styles.formSection}>
        <h2>Edit Profiles</h2>

        {profiles.map((profile, index) => (
          <div key={profile.id} className={styles.profileAnnouncementBlock}>
            {/* PROFILE HEADER */}
            <h3 className={styles.profileNameHeader}>
              {profile.name || `Profile ${index + 1}`}
            </h3>

            {/* AVAILABILITY */}
            <div className={styles.inputRow}>
              <label className={styles.fieldLabel}>On Vacation?</label>
              <input
                type="checkbox"
                checked={profile.vacation === true}
                onChange={(e) => {
                  const newProfiles = [...profiles];
                  newProfiles[index].vacation = e.target.checked ? true : false;
                  setProfiles(newProfiles);
                }}
                className={styles.checkboxField}
              />
            </div>

            {/* LOCATION NAME */}
            <div className={styles.inputRow}>
              <label className={styles.fieldLabel}>Location Name</label>
              <input
                type="text"
                value={profile.location?.name || ""}
                onChange={(e) => {
                  const newProfiles = [...profiles];
                  newProfiles[index].location = {
                    ...newProfiles[index].location,
                    name: e.target.value,
                  };
                  setProfiles(newProfiles);
                }}
                className={styles.inputField}
              />
            </div>

            {/* GOOGLE MAPS URL */}
            <div className={styles.inputRow}>
              <label className={styles.fieldLabel}>Google Maps URL</label>
              <input
                type="text"
                value={profile.location?.googleMapsUrl || ""}
                onChange={(e) => {
                  const newProfiles = [...profiles];
                  newProfiles[index].location = {
                    ...newProfiles[index].location,
                    googleMapsUrl: e.target.value,
                  };
                  setProfiles(newProfiles);
                }}
                className={styles.inputField}
              />
            </div>

            <hr className={styles.sectionLine} />

            {/* ANNOUNCEMENTS */}
            {(profile.announcements || []).map((item, aIndex) => (
              <div
                key={item.id}
                className={`${styles.inputRow} ${
                  item.queuedForDeletion ? styles.queuedDelete : ""
                }`}
              >
                <label className={styles.fieldLabel}>
                  Announcement {aIndex + 1}
                </label>

                <input
                  type="text"
                  value={item.announcement}
                  onChange={(e) => {
                    const newProfiles = [...profiles];
                    newProfiles[index].announcements[aIndex].announcement =
                      e.target.value;
                    setProfiles(newProfiles);
                  }}
                  className={styles.inputField}
                />

                <span
                  className={styles.deleteIcon}
                  onClick={() => {
                    const newProfiles = [...profiles];
                    newProfiles[index].announcements[aIndex].queuedForDeletion =
                      !newProfiles[index].announcements[aIndex]
                        .queuedForDeletion;
                    setProfiles(newProfiles);
                  }}
                >
                  {item.queuedForDeletion ? "↩️" : "❌"}
                </span>
              </div>
            ))}

            {/* ADD ANNOUNCEMENT */}
            <div className={styles.addRow}>
              <button
                className={styles.button}
                onClick={() => {
                  const newProfiles = [...profiles];
                  const nextId = profile.announcements?.length
                    ? Math.max(
                        ...profile.announcements.map((a) =>
                          typeof a.id === "number" ? a.id : 0,
                        ),
                      ) + 1
                    : 1;

                  const newAnnouncement = {
                    id: nextId,
                    announcement: "",
                  };

                  if (!newProfiles[index].announcements) {
                    newProfiles[index].announcements = [];
                  }

                  newProfiles[index].announcements.push(newAnnouncement);
                  setProfiles(newProfiles);
                }}
              >
                + Announcement
              </button>
            </div>

            {/* SAVE BUTTON */}
            <div className={styles.saveRow}>
              <button
                className={styles.button}
                onClick={handleSaveProfile(index)}
                disabled={savingProfiles}
              >
                {savingProfiles ? "Saving..." : "Save Changes"}
              </button>
            </div>

            {profile.__status && (
              <div className={styles.saveFeedback}>{profile.__status}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default AdminPage;
